import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { sortSectorsByCode } from '../../utils/sectorOrder';

const SECTORS_COLLECTION = 'sectors';

const normalizeSector = (docSnapshot) => {
  const data = docSnapshot.data();
  return {
    id: docSnapshot.id,
    ...data,
    polygon: Array.isArray(data.polygon) ? data.polygon : [],
    active: data.active !== false,
    dispatchFromQueueEnd: data.dispatchFromQueueEnd === true
  };
};

export const fetchSectors = createAsyncThunk(
  'sectors/fetchSectors',
  async (_, { rejectWithValue }) => {
    try {
      const sectorsQuery = query(
        collection(db, SECTORS_COLLECTION),
        orderBy('sortOrder', 'asc')
      );
      const snapshot = await getDocs(sectorsQuery);
      return snapshot.docs.map(normalizeSector);
    } catch (error) {
      console.error('Fehler beim Laden der Sektoren:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const createSector = createAsyncThunk(
  'sectors/createSector',
  async (sectorData, { rejectWithValue }) => {
    try {
      const code = (sectorData.code || '').trim();
      if (code) {
        const duplicateSnapshot = await getDocs(query(
          collection(db, SECTORS_COLLECTION),
          where('code', '==', code)
        ));
        if (!duplicateSnapshot.empty) {
          throw new Error(`Sektornummer ${code} ist bereits vergeben`);
        }
      }

      const payload = {
        ...sectorData,
        name: (sectorData.name || '').trim(),
        code,
        active: sectorData.active !== false,
        dispatchFromQueueEnd: sectorData.dispatchFromQueueEnd === true,
        sortOrder: Number(sectorData.sortOrder) || 0,
        priority: Number(sectorData.priority) || 100,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, SECTORS_COLLECTION), payload);
      return { id: docRef.id, ...payload };
    } catch (error) {
      console.error('Fehler beim Erstellen des Sektors:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateSector = createAsyncThunk(
  'sectors/updateSector',
  async ({ id, ...sectorData }, { rejectWithValue }) => {
    try {
      const code = (sectorData.code || '').trim();
      if (code) {
        const duplicateSnapshot = await getDocs(query(
          collection(db, SECTORS_COLLECTION),
          where('code', '==', code)
        ));
        const duplicate = duplicateSnapshot.docs.find(docSnapshot => docSnapshot.id !== id);
        if (duplicate) {
          throw new Error(`Sektornummer ${code} ist bereits vergeben`);
        }
      }

      const payload = {
        ...sectorData,
        name: (sectorData.name || '').trim(),
        code,
        active: sectorData.active !== false,
        dispatchFromQueueEnd: sectorData.dispatchFromQueueEnd === true,
        sortOrder: Number(sectorData.sortOrder) || 0,
        priority: Number(sectorData.priority) || 100,
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, SECTORS_COLLECTION, id), payload);
      return { id, ...payload };
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Sektors:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSector = createAsyncThunk(
  'sectors/deleteSector',
  async (id, { rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, SECTORS_COLLECTION, id));
      return id;
    } catch (error) {
      console.error('Fehler beim Löschen des Sektors:', error);
      return rejectWithValue(error.message);
    }
  }
);

const sectorsSlice = createSlice({
  name: 'sectors',
  initialState: {
    sectors: [],
    isLoading: false,
    isSaving: false,
    error: null
  },
  reducers: {
    clearSectorsError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSectors.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSectors.fulfilled, (state, action) => {
        state.isLoading = false;
        // Anzeige-Reihenfolge einmal zentral herstellen: Sektorverwaltung,
        // LiveMap und alle weiteren Ansichten lesen dieselbe Liste und zeigen
        // damit dieselbe Reihenfolge. utils/sectorOrder.js erklaert, warum
        // nach Nummer und nicht nach sortOrder sortiert wird.
        state.sectors = sortSectorsByCode(action.payload);
      })
      .addCase(fetchSectors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createSector.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createSector.fulfilled, (state, action) => {
        state.isSaving = false;
        // Neu angelegter Sektor haengt sich sonst unten an, statt an seiner
        // Nummer einsortiert zu werden.
        state.sectors = sortSectorsByCode([...state.sectors, action.payload]);
      })
      .addCase(createSector.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      .addCase(updateSector.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateSector.fulfilled, (state, action) => {
        state.isSaving = false;
        // Neu sortieren, weil die Nummer selbst geaendert worden sein kann.
        state.sectors = sortSectorsByCode(
          state.sectors.map(sector =>
            sector.id === action.payload.id ? action.payload : sector
          )
        );
      })
      .addCase(updateSector.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload;
      })
      .addCase(deleteSector.fulfilled, (state, action) => {
        state.sectors = state.sectors.filter(sector => sector.id !== action.payload);
      })
      .addCase(deleteSector.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const { clearSectorsError } = sectorsSlice.actions;
export default sectorsSlice.reducer;
